import { Controller } from "@hotwired/stimulus"

const SEARCH_DEBOUNCE = 250
const MIN_QUERY = 2
const MAX_QUERY = 80
const GROUPS = ["orders", "customers", "purchase_orders", "products"]

export default class extends Controller {
  static targets = ["dialog", "input", "list"]

  connect() {
    this.searchTimeout = null
    this.inFlight = null   // AbortController for the request still running
    this.searchSeq = 0     // monotonic: a response older than the latest query is dropped
    this.records = null
    this.activeIndex = -1
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    clearTimeout(this.searchTimeout)
    this.abort()
  }

  handleKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.open()
      return
    }
    if (!this.dialogTarget.open) return

    if (event.key === "Escape") {
      this.close()
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      this.move(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.move(-1)
    } else if (event.key === "Enter") {
      const row = this.rows()[this.activeIndex]
      if (!row) return
      event.preventDefault()
      this.go(row.dataset.url)
    }
  }

  open() {
    this.dialogTarget.showModal()
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.filter()
  }

  close() {
    this.dialogTarget.close()
  }

  closeOnBackdrop(event) {
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  filter() {
    const query = this.inputTarget.value.toLowerCase().trim()
    const items = this.listTarget.querySelectorAll("[data-command]")

    items.forEach(item => {
      const text = item.dataset.command.toLowerCase()
      item.style.display = (!query || text.includes(query)) ? "" : "none"
    })

    this.scheduleSearch(this.inputTarget.value.trim())
    this.highlight(-1)
  }

  navigate(event) {
    // Rows are real <a href>; without this Turbo's own click listener
    // proposes a second visit and cancels ours (two renders per click).
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    if (url) this.go(url)
  }

  go(url) {
    if (!url) return
    this.close()
    // Turbo.visit (not location.href) so the workspace tab strip, which
    // listens for turbo:load, sees the record open as a tab.
    if (window.Turbo && typeof window.Turbo.visit === "function") {
      window.Turbo.visit(url)
    } else {
      window.location.href = url
    }
  }

  // --- remote record search ------------------------------------------------

  scheduleSearch(query) {
    clearTimeout(this.searchTimeout)

    if (query.length < MIN_QUERY) {
      this.abort()
      this.searchSeq += 1   // anything still resolving is now stale
      this.clearRecords()
      return
    }

    this.searchTimeout = setTimeout(() => this.search(query.slice(0, MAX_QUERY)), SEARCH_DEBOUNCE)
  }

  search(query) {
    this.abort()
    const seq = ++this.searchSeq
    this.inFlight = new AbortController()

    fetch(`/search.json?q=${encodeURIComponent(query)}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: this.inFlight.signal
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (seq !== this.searchSeq) return   // a newer query already answered
        if (data) this.renderRecords(data)
      })
      .catch(() => {})   // aborted or offline: keep whatever is on screen
  }

  abort() {
    if (this.inFlight) this.inFlight.abort()
    this.inFlight = null
  }

  renderRecords(data) {
    const results = GROUPS.flatMap((group) => (Array.isArray(data[group]) ? data[group] : []))
    if (results.length === 0) {
      this.clearRecords()
      return
    }

    const container = this.recordsContainer()
    container.replaceChildren()

    const heading = document.createElement("div")
    heading.className = "command-palette-group-label"
    heading.textContent = "Records"
    container.appendChild(heading)

    results.forEach((item) => {
      const row = this.recordRow(item)
      if (row) container.appendChild(row)
    })
  }

  recordsContainer() {
    if (!this.records || !this.records.isConnected) {
      this.records = document.createElement("div")
      this.records.className = "command-palette-records"
      this.listTarget.appendChild(this.records)
    }
    return this.records
  }

  clearRecords() {
    if (this.records) this.records.remove()
    this.records = null
  }

  recordRow(item) {
    const row = document.createElement("a")
    row.className = "command-palette-item"
    // Defense in depth: only same-origin paths ever become a row.
    if (typeof item.url !== "string" || !/^\/(?!\/)/.test(item.url)) return null
    row.href = item.url
    row.dataset.url = item.url
    row.dataset.action = "click->command-palette#navigate"

    // textContent, never innerHTML: labels are user-supplied record data.
    const label = document.createElement("span")
    label.textContent = item.label || item.url
    row.appendChild(label)

    if (item.sub) {
      const sub = document.createElement("span")
      sub.className = "command-palette-item-sub"
      sub.textContent = item.sub
      row.appendChild(sub)
    }

    return row
  }

  // --- keyboard navigation -------------------------------------------------

  // Static commands still visible after filtering, then the remote rows.
  rows() {
    return Array.from(this.listTarget.querySelectorAll("[data-url]"))
      .filter((row) => row.style.display !== "none")
  }

  move(step) {
    const rows = this.rows()
    if (rows.length === 0) return

    const next = this.activeIndex < 0
      ? (step > 0 ? 0 : rows.length - 1)
      : (this.activeIndex + step + rows.length) % rows.length
    this.highlight(next, rows)
  }

  highlight(index, rows = this.rows()) {
    this.activeIndex = index
    rows.forEach((row, i) => row.classList.toggle("is-active", i === index))
    if (rows[index] && rows[index].scrollIntoView) rows[index].scrollIntoView({ block: "nearest" })
  }
}
